import { useParams, useSearchParams } from 'react-router-dom'

import { Box, Button, Grid } from '@factorialco/deprecated-design-system'
import { PerformanceProcessTemplateEditSettingsPageDocument } from 'generated/resources/graphql'
import { keyBy } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { ScoreModeManager } from 'modules/performance_process/components/ScoreModeManager'
import { EditionWindow } from 'modules/performance_process/components/review/EditionWindow/EditionWindow'
import { settingsSections } from 'modules/performance_process/components/review/EditionWindow/editionWindowOptions'
import { CustomTemplateSettingsBasicInfo } from 'modules/performance_process/components/templates/CustomTemplateSettingsBasicInfo'
import { CustomTemplateSettingsFeatures } from 'modules/performance_process/components/templates/CustomTemplateSettingsFeatures'
import { CustomTemplateSettingsReviewTypes } from 'modules/performance_process/components/templates/CustomTemplateSettingsReviewTypes'
import { CustomTemplateSettingsTargetGroup } from 'modules/performance_process/components/templates/CustomTemplateSettingsTargetGroup'
import { CustomTemplateSettingsVisibility } from 'modules/performance_process/components/templates/CustomTemplateSettingsVisibility'
import { useNewPerformanceModuleContext } from 'modules/performance_process/pages/ReviewsRootPage'

const TemplateSettingsPage = () => {
  const { templateId } = useParams()
  const { navigation } = useNavigation()
  const { company } = useCurrentSession()

  const [searchParams, setSearchParams] = useSearchParams()
  const { data, error, loading } = useQuery(PerformanceProcessTemplateEditSettingsPageDocument, {
    variables: {
      id: templateId ?? '',
      companyId: company.id,
    },
  })
  const { hasCustomScoreConfigurationEnabled } = useNewPerformanceModuleContext()

  const features = keyBy(data?.features?.featuresConnection?.nodes || [], 'name')
  const isEsignatureEnabled = features['esignature']?.enabled ?? false

  const template = data?.performance.reviewProcessCustomTemplate
  const settingOptions = settingsSections(['timeline', 'target', 'questions_view'])
  const selectedSection = searchParams.get('section') ?? 'basic'

  const handleOnChange = (value: string) => {
    setSearchParams({ section: value })
  }

  return (
    <ResponsivePage.Body
      error={error || !template}
      loading={loading}
      authorized={template?._policies?.manage.can}
      showHeader={false}
      widget={
        <Button.Router
          to={navigation.performanceProcess.reviews.templates.template.fullPath({
            templateId: templateId ?? '',
          })}
        >
          {i18n.t('performance_process.reviews.templates.edit.back_to_overview')}
        </Button.Router>
      }
    >
      {template && (
        <Grid variant="fixed">
          <Box gridColSpan={{ xs: 4 }} gap="s8">
            {settingOptions.map((option) => {
              return (
                <EditionWindow
                  key={option.label}
                  label={option.label}
                  value={option.value}
                  selected={option.value === selectedSection}
                  icon={option.icon}
                  onChange={handleOnChange}
                  complete
                />
              )
            })}
          </Box>
          <Box gridColSpan={{ xs: 6 }} padding="s16">
            {selectedSection === 'basic' && <CustomTemplateSettingsBasicInfo template={template} />}
            {selectedSection === 'review_types' && (
              <CustomTemplateSettingsReviewTypes template={template} />
            )}
            {selectedSection === 'visibility' && (
              <CustomTemplateSettingsVisibility template={template} />
            )}
            {selectedSection === 'scoring_system' && (
              <ScoreModeManager
                mode={template.scoreConfiguration?.mode}
                questionnaireableId={template.id}
                questionnaireableType={'review_process_custom_template'}
                hasCustomScoreConfigurationEnabled={hasCustomScoreConfigurationEnabled}
              />
            )}
            {selectedSection === 'features' && (
              <CustomTemplateSettingsFeatures
                template={template}
                isEsignatureEnabled={isEsignatureEnabled}
              />
            )}
            {selectedSection === 'target_group' && (
              <CustomTemplateSettingsTargetGroup template={template} />
            )}
          </Box>
        </Grid>
      )}
    </ResponsivePage.Body>
  )
}

export default TemplateSettingsPage
